import React, { useState, useEffect, useRef } from 'react'
import MaterialGroupsPanel from './MaterialGroupsPanel'
import PartCard, { partsGridStyle } from './PartCard'

/**
 * Collapsible three-section accordion for project setup.
 *
 * Replaces the three stacked cards (Project Info / Material / Parts) with
 * a compact accordion that automatically collapses once a nest is generated
 * so the results take centre stage. Each section is independently toggleable
 * and displays a single-line breadcrumb when collapsed so the operator can
 * still see the current state without expanding.
 */
export default function NesterSetupAccordion({
  colors,
  mode,
  // project info
  projectName, setProjectName,
  customerId, setCustomerId, customerName, setCustomerName,
  customers,
  status, setStatus,
  description, setDescription,
  // material
  materialType, setMaterialType,
  surfaceFinish, setSurfaceFinish,
  thicknessDisplay, thicknessIn, handleThicknessChange,
  sheetWidth, setSheetWidth, sheetLength, setSheetLength,
  feedrateIpm, setFeedrateIpm,
  pierceTimeSec, setPierceTimeSec,
  gasType, setGasType,
  nozzleCleanPierces, setNozzleCleanPierces,
  nozzleCleanSec, setNozzleCleanSec,
  alignmentTimeSec, setAlignmentTimeSec,
  customerSupplied, setCustomerSupplied,
  costPerSheet, setCostPerSheet,
  costPerLb, setCostPerLb,
  gasFlowCfh, setGasFlowCfh,
  gasCostPerCf, setGasCostPerCf,
  consumableRate, setConsumableRate,
  // reference data
  materialTypes, thicknesses, surfaceFinishes, laserSpecs,
  // material groups
  materialGroups, handleGroupsChange, convertToGroups,
  // parts
  parts,
  selectedProject,
  uploading, uploadGroupId, setUploadGroupId,
  handleDxfUpload, fileInputRef,
  partGap, setPartGap, edgeMargin, setEdgeMargin,
  fillResolutions,
  updatePartField, updatePartQuantity, deletePart,
  // nest state — drives auto-collapse behaviour
  hasNestResult,
  // surfaces backend errors (e.g. missing laser_tech_specs 400) into the
  // parent's error banner instead of hiding them in console
  onError,
}) {
  const [open, setOpen] = useState({
    info: !selectedProject || !hasNestResult,
    material: !hasNestResult,
    parts: !hasNestResult,
  })
  const prevHad = useRef(hasNestResult)

  useEffect(() => {
    // Auto-collapse everything on the first successful nest generation,
    // but only once — don't fight the operator if they re-open sections.
    if (hasNestResult && !prevHad.current) {
      setOpen({ info: false, material: false, parts: false })
    }
    prevHad.current = hasNestResult
  }, [hasNestResult])

  const filteredThicknesses = materialType
    ? thicknesses.filter(t => t.material_type?.toLowerCase() === materialType.toLowerCase())
    : thicknesses

  const unassignedParts = materialGroups.length > 0
    ? parts.filter(p => !p.material_group_id)
    : []

  const infoSummary = [
    projectName || 'Untitled project',
    customerName,
    status && status !== 'draft' ? status : null,
  ].filter(Boolean).join(' · ')

  const matSummary = materialGroups.length > 0
    ? `${materialGroups.length} group${materialGroups.length === 1 ? '' : 's'} · ${materialGroups.map(g => `${g.material_type || '?'} ${g.thickness_display || ''}`).join(', ')}`
    : [
        materialType,
        thicknessDisplay,
        sheetWidth && sheetLength ? `${sheetWidth}×${sheetLength}"` : null,
        feedrateIpm ? `${feedrateIpm} IPM` : null,
        gasType,
      ].filter(Boolean).join(' · ') || 'Not set'

  const partsSummary = parts.length === 0
    ? 'No parts yet'
    : `${parts.length} part${parts.length === 1 ? '' : 's'}${unassignedParts.length > 0 ? ` · ${unassignedParts.length} unassigned` : ''}`

  return (
    <div style={{
      background: colors.panel,
      border: `1px solid ${colors.border}`,
      borderRadius: 8,
      boxShadow: colors.shadow,
      marginBottom: 8,
    }}>
      {/* ── Section 1: Project Info ────────────────────────────── */}
      <AccordionSection
        colors={colors}
        title="Project Info"
        summary={infoSummary}
        open={open.info}
        onToggle={() => setOpen(o => ({ ...o, info: !o.info }))}
        isFirst
      >
        <div style={sty.row}>
          <div style={sty.grp}>
            <label style={sty.label(colors)}>Project Name *</label>
            <input value={projectName} onChange={e => setProjectName(e.target.value)}
              style={sty.input(colors)} placeholder="e.g., Smith Brackets Quote" />
          </div>
          <div style={sty.grp}>
            <label style={sty.label(colors)}>Customer</label>
            <select value={customerId}
              onChange={e => {
                setCustomerId(e.target.value)
                const cust = customers.find(c => c.customer_id === parseInt(e.target.value))
                setCustomerName(cust?.name || '')
              }}
              style={sty.input(colors)}>
              <option value="">-- None --</option>
              {customers.map(c => (
                <option key={c.customer_id} value={c.customer_id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div style={{ ...sty.grp, flex: '0 0 130px' }}>
            <label style={sty.label(colors)}>Status</label>
            <select value={status} onChange={e => setStatus(e.target.value)} style={sty.input(colors)}>
              <option value="draft">Draft</option>
              <option value="quoted">Quoted</option>
              <option value="won">Won</option>
              <option value="lost">Lost</option>
            </select>
          </div>
        </div>
        <div style={sty.grp}>
          <label style={sty.label(colors)}>Description / Notes</label>
          <textarea rows={2} value={description}
            onChange={e => setDescription(e.target.value)}
            style={{ ...sty.input(colors), minHeight: 44, resize: 'vertical', fontFamily: 'inherit' }}
            placeholder="Optional notes…" />
        </div>
      </AccordionSection>

      {/* ── Section 2: Material & Machine ──────────────────────── */}
      <AccordionSection
        colors={colors}
        title="Material & Machine"
        summary={matSummary}
        open={open.material}
        onToggle={() => setOpen(o => ({ ...o, material: !o.material }))}
      >
        {materialGroups.length > 0 ? (
          <MaterialGroupsPanel
            projectId={selectedProject?.project_id}
            groups={materialGroups}
            onGroupsChange={handleGroupsChange}
            materialTypes={materialTypes}
            thicknesses={thicknesses}
            laserSpecs={laserSpecs}
            surfaceFinishes={surfaceFinishes}
            parts={parts}
            fillResolutions={fillResolutions}
            onUpdatePartField={updatePartField}
            onUpdatePartQuantity={updatePartQuantity}
            onDeletePart={deletePart}
            onError={onError}
          />
        ) : (
          <SingleMaterialForm
            colors={colors}
            selectedProject={selectedProject}
            materialType={materialType} setMaterialType={setMaterialType}
            materialTypes={materialTypes}
            surfaceFinish={surfaceFinish} setSurfaceFinish={setSurfaceFinish}
            surfaceFinishes={surfaceFinishes}
            thicknessDisplay={thicknessDisplay}
            thicknessIn={thicknessIn}
            handleThicknessChange={handleThicknessChange}
            filteredThicknesses={filteredThicknesses}
            sheetWidth={sheetWidth} setSheetWidth={setSheetWidth}
            sheetLength={sheetLength} setSheetLength={setSheetLength}
            feedrateIpm={feedrateIpm} setFeedrateIpm={setFeedrateIpm}
            pierceTimeSec={pierceTimeSec} setPierceTimeSec={setPierceTimeSec}
            gasType={gasType} setGasType={setGasType}
            nozzleCleanPierces={nozzleCleanPierces} setNozzleCleanPierces={setNozzleCleanPierces}
            nozzleCleanSec={nozzleCleanSec} setNozzleCleanSec={setNozzleCleanSec}
            alignmentTimeSec={alignmentTimeSec} setAlignmentTimeSec={setAlignmentTimeSec}
            customerSupplied={customerSupplied} setCustomerSupplied={setCustomerSupplied}
            costPerSheet={costPerSheet} setCostPerSheet={setCostPerSheet}
            costPerLb={costPerLb} setCostPerLb={setCostPerLb}
            gasFlowCfh={gasFlowCfh} setGasFlowCfh={setGasFlowCfh}
            gasCostPerCf={gasCostPerCf} setGasCostPerCf={setGasCostPerCf}
            consumableRate={consumableRate} setConsumableRate={setConsumableRate}
            partGap={partGap} setPartGap={setPartGap}
            edgeMargin={edgeMargin} setEdgeMargin={setEdgeMargin}
            convertToGroups={convertToGroups}
          />
        )}
      </AccordionSection>

      {/* ── Section 3: Parts ───────────────────────────────────── */}
      <AccordionSection
        colors={colors}
        title="Parts"
        summary={selectedProject ? partsSummary : 'Save the project to upload parts'}
        open={open.parts}
        onToggle={() => setOpen(o => ({ ...o, parts: !o.parts }))}
        isLast
      >
        {!selectedProject && (
          <div style={{
            padding: '10px 12px', marginBottom: 10,
            background: colors.warnSoft, border: `1px solid ${colors.warn}`,
            borderRadius: 6, fontSize: 12, color: colors.text,
          }}>
            Enter a project name above and click <strong>Create project</strong> (or ⌘S) to enable DXF uploads.
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
          <input type="file" ref={fileInputRef} accept=".dxf" multiple
            disabled={!selectedProject}
            onChange={handleDxfUpload}
            style={{
              ...sty.input(colors), maxWidth: 360, padding: '3px 6px',
              opacity: selectedProject ? 1 : 0.5,
              cursor: selectedProject ? 'pointer' : 'not-allowed',
            }} />
          {materialGroups.length > 1 && (
            <select style={{ ...sty.input(colors), width: 220 }}
              value={uploadGroupId || ''}
              onChange={e => setUploadGroupId(e.target.value ? parseInt(e.target.value) : null)}>
              <option value="">Select group…</option>
              {materialGroups.map(g => (
                <option key={g.group_id} value={g.group_id}>
                  {g.group_name || `Group ${g.group_id}`}
                </option>
              ))}
            </select>
          )}
          {uploading && <span style={{ fontSize: 12, color: colors.textMuted }}>Uploading…</span>}
        </div>

        {materialGroups.length > 0 ? (
          unassignedParts.length > 0 && (
            <div>
              <div style={{ fontSize: 12, color: colors.warn, fontWeight: 600, marginBottom: 6 }}>
                Unassigned ({unassignedParts.length}) — assign below or edit in each group
              </div>
              <div style={partsGridStyle}>
                {unassignedParts.map(part => (
                  <PartCard key={part.part_id} part={part}
                    materialGroups={materialGroups}
                    sheetWidth={parseFloat(sheetWidth) || 0}
                    sheetLength={parseFloat(sheetLength) || 0}
                    edgeMargin={parseFloat(edgeMargin) || 0}
                    showGroupDropdown={true}
                    showColoredBorder={true}
                    fillResolved={fillResolutions[part.part_id]}
                    onUpdateField={updatePartField}
                    onUpdateQuantity={updatePartQuantity}
                    onDelete={deletePart}
                  />
                ))}
              </div>
            </div>
          )
        ) : (
          parts.length > 0 && (
            <div style={partsGridStyle}>
              {parts.map(part => (
                <PartCard key={part.part_id} part={part}
                  materialGroups={[]}
                  sheetWidth={parseFloat(sheetWidth) || 0}
                  sheetLength={parseFloat(sheetLength) || 0}
                  edgeMargin={parseFloat(edgeMargin) || 0}
                  showGroupDropdown={false}
                  showColoredBorder={false}
                  fillResolved={fillResolutions[part.part_id]}
                  onUpdateField={updatePartField}
                  onUpdateQuantity={updatePartQuantity}
                  onDelete={deletePart}
                />
              ))}
            </div>
          )
        )}
      </AccordionSection>
    </div>
  )
}

function AccordionSection({ colors, title, summary, open, onToggle, children, isFirst, isLast }) {
  return (
    <div style={{
      borderTop: isFirst ? 'none' : `1px solid ${colors.border}`,
    }}>
      <button onClick={onToggle}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 14px',
          border: 'none', background: open ? colors.panelAlt : 'transparent',
          color: colors.text, textAlign: 'left', cursor: 'pointer',
          borderRadius: isFirst
            ? (open ? '8px 8px 0 0' : '8px 8px 0 0')
            : isLast && !open
              ? '0 0 8px 8px'
              : 0,
          transition: 'background 0.15s',
        }}>
        <span style={{ fontSize: 11, color: colors.textMuted, width: 10 }}>
          {open ? '▼' : '▶'}
        </span>
        <span style={{ fontSize: 13, fontWeight: 600 }}>{title}</span>
        <span style={{
          flex: 1, fontSize: 12, color: colors.textMuted,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {summary}
        </span>
      </button>
      {open && (
        <div style={{
          padding: '8px 14px 14px',
          borderTop: `1px solid ${colors.border}`,
        }}>
          {children}
        </div>
      )}
    </div>
  )
}

function SingleMaterialForm(props) {
  const {
    colors, selectedProject,
    materialType, setMaterialType, materialTypes,
    surfaceFinish, setSurfaceFinish, surfaceFinishes,
    thicknessDisplay, handleThicknessChange, filteredThicknesses,
    sheetWidth, setSheetWidth, sheetLength, setSheetLength,
    feedrateIpm, setFeedrateIpm,
    pierceTimeSec, setPierceTimeSec,
    gasType, setGasType,
    nozzleCleanPierces, setNozzleCleanPierces,
    nozzleCleanSec, setNozzleCleanSec,
    alignmentTimeSec, setAlignmentTimeSec,
    customerSupplied, setCustomerSupplied,
    costPerSheet, setCostPerSheet,
    costPerLb, setCostPerLb,
    gasFlowCfh, setGasFlowCfh,
    gasCostPerCf, setGasCostPerCf,
    consumableRate, setConsumableRate,
    partGap, setPartGap, edgeMargin, setEdgeMargin,
    convertToGroups,
  } = props

  const fillStyle = (val) => val
    ? { ...sty.input(colors), background: colors.successSoft }
    : { ...sty.input(colors), background: colors.warnSoft }

  return (
    <div>
      {selectedProject && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
          <button onClick={convertToGroups}
            style={{
              padding: '3px 10px', fontSize: 11, fontWeight: 600,
              border: `1px solid ${colors.accent}`, color: colors.accent,
              background: 'transparent', borderRadius: 4, cursor: 'pointer',
            }}
            title="Use multiple materials in one project — each nests on its own sheet type">
            + Multi-Material
          </button>
        </div>
      )}
      <div style={sty.row}>
        <div style={sty.grp}>
          <label style={sty.label(colors)}>Material Type</label>
          <select value={materialType}
            onChange={e => { setMaterialType(e.target.value) }}
            style={sty.input(colors)}>
            <option value="">Select material…</option>
            {[...new Set(materialTypes.map(m => m.type_name || m.material_type))].sort().map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div style={sty.grp}>
          <label style={sty.label(colors)}>Surface Finish</label>
          <select value={surfaceFinish} onChange={e => setSurfaceFinish(e.target.value)} style={sty.input(colors)}>
            <option value="">-- None --</option>
            {surfaceFinishes.map(sf => <option key={sf} value={sf}>{sf}</option>)}
          </select>
        </div>
        <div style={sty.grp}>
          <label style={sty.label(colors)}>Thickness</label>
          <select value={thicknessDisplay}
            onChange={e => {
              const sel = filteredThicknesses.find(t => t.display_thickness === e.target.value)
              handleThicknessChange(e.target.value, sel?.thickness_in?.toString() || '')
            }}
            style={sty.input(colors)}>
            <option value="">Select thickness…</option>
            {filteredThicknesses.map(t => (
              <option key={t.thickness_id} value={t.display_thickness}>
                {t.display_thickness} ({t.thickness_in}")
              </option>
            ))}
          </select>
        </div>
        <div style={sty.grp}>
          <label style={sty.label(colors)}>Gas Type</label>
          <input value={gasType} onChange={e => setGasType(e.target.value)}
            style={sty.input(colors)} placeholder="e.g., Nitrogen" />
        </div>
      </div>
      <div style={sty.row}>
        <div style={sty.grp}><label style={sty.label(colors)}>Sheet Width (in)</label>
          <input type="number" value={sheetWidth} onChange={e => setSheetWidth(e.target.value)} style={sty.input(colors)} /></div>
        <div style={sty.grp}><label style={sty.label(colors)}>Sheet Length (in)</label>
          <input type="number" value={sheetLength} onChange={e => setSheetLength(e.target.value)} style={sty.input(colors)} /></div>
        <div style={sty.grp}><label style={sty.label(colors)}>Feed Rate (IPM)</label>
          <input type="number" value={feedrateIpm} onChange={e => setFeedrateIpm(e.target.value)} style={fillStyle(feedrateIpm)} /></div>
        <div style={sty.grp}><label style={sty.label(colors)}>Pierce Time (sec)</label>
          <input type="number" value={pierceTimeSec} onChange={e => setPierceTimeSec(e.target.value)} style={fillStyle(pierceTimeSec)} /></div>
        <div style={sty.grp}><label style={sty.label(colors)}>Part Gap (in)</label>
          <input type="number" step="0.0625" value={partGap} onChange={e => setPartGap(e.target.value)} style={sty.input(colors)} /></div>
        <div style={sty.grp}><label style={sty.label(colors)}>Edge Margin (in)</label>
          <input type="number" step="0.0625" value={edgeMargin} onChange={e => setEdgeMargin(e.target.value)} style={sty.input(colors)} /></div>
      </div>
      <div style={sty.row}>
        <div style={sty.grp}><label style={sty.label(colors)}>Nozzle Clean (pierces)</label>
          <input type="number" value={nozzleCleanPierces} onChange={e => setNozzleCleanPierces(e.target.value)} style={sty.input(colors)} /></div>
        <div style={sty.grp}><label style={sty.label(colors)}>Clean Time (sec)</label>
          <input type="number" value={nozzleCleanSec} onChange={e => setNozzleCleanSec(e.target.value)} style={sty.input(colors)} /></div>
        <div style={sty.grp}><label style={sty.label(colors)}>Alignment (sec)</label>
          <input type="number" value={alignmentTimeSec} onChange={e => setAlignmentTimeSec(e.target.value)} style={sty.input(colors)} /></div>
      </div>

      <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${colors.border}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Cost Inputs (auto-filled from JobQ)
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer', color: colors.text }}>
            <input type="checkbox" checked={customerSupplied}
              onChange={e => { setCustomerSupplied(e.target.checked); if (e.target.checked) { setCostPerSheet(''); setCostPerLb('') } }} />
            Customer Supplied Material
          </label>
        </div>
        {customerSupplied && (
          <div style={{ fontSize: 12, color: colors.success, fontStyle: 'italic', marginBottom: 6 }}>
            Material cost excluded — customer is supplying the material.
          </div>
        )}
        <div style={sty.row}>
          <div style={sty.grp}><label style={sty.label(colors)}>$/Sheet</label>
            <input type="number" step="0.01" value={costPerSheet} onChange={e => setCostPerSheet(e.target.value)}
              disabled={customerSupplied}
              style={customerSupplied ? { ...sty.input(colors), opacity: 0.5 } : costPerSheet ? { ...sty.input(colors), background: colors.successSoft } : sty.input(colors)} /></div>
          <div style={sty.grp}><label style={sty.label(colors)}>$/lb</label>
            <input type="number" step="0.01" value={costPerLb} onChange={e => setCostPerLb(e.target.value)}
              disabled={customerSupplied}
              style={customerSupplied ? { ...sty.input(colors), opacity: 0.5 } : costPerLb ? { ...sty.input(colors), background: colors.successSoft } : sty.input(colors)} /></div>
          <div style={sty.grp}><label style={sty.label(colors)}>Gas Flow (CFH)</label>
            <input type="number" value={gasFlowCfh} onChange={e => setGasFlowCfh(e.target.value)}
              style={gasFlowCfh ? { ...sty.input(colors), background: colors.successSoft } : sty.input(colors)} /></div>
          <div style={sty.grp}><label style={sty.label(colors)}>Gas $/CF</label>
            <input type="number" step="0.01" value={gasCostPerCf} onChange={e => setGasCostPerCf(e.target.value)}
              style={gasCostPerCf ? { ...sty.input(colors), background: colors.successSoft } : sty.input(colors)} /></div>
          <div style={sty.grp}><label style={sty.label(colors)}>Consumable $/hr</label>
            <input type="number" step="0.01" value={consumableRate} onChange={e => setConsumableRate(e.target.value)}
              style={consumableRate ? { ...sty.input(colors), background: colors.successSoft } : sty.input(colors)} /></div>
        </div>
      </div>
    </div>
  )
}

const sty = {
  row: { display: 'flex', gap: 10, marginBottom: 8, flexWrap: 'wrap' },
  grp: { flex: 1, minWidth: 140 },
  label: (c) => ({
    display: 'block',
    fontSize: 11, fontWeight: 600, color: c.textMuted,
    textTransform: 'uppercase', letterSpacing: '0.04em',
    marginBottom: 3,
  }),
  input: (c) => ({
    width: '100%',
    padding: '4px 8px', borderRadius: 4,
    border: `1px solid ${c.border}`,
    background: c.panel, color: c.text,
    fontSize: 13,
    boxSizing: 'border-box',
  }),
}
